/**
 * Single source of truth for all route paths.
 *
 * URL загвар (CLAUDE.md §4.0):
 *   /                       — public landing
 *   /login                  — public login
 *   /select-scope           — login дараа scope сонгох
 *   /:tenantId/:companyId/<page>   — бүх authenticated page (Guid + Guid prefix)
 *
 * Шинэ хуудас нэмэх:
 *   1. Доорх `scopedRoutes` map-д RELATIVE path (`'foo'`) нэмнэ.
 *   2. Navigate хийхдээ `useScopedNavigate()` эсвэл `buildScopedPath()`.
 *   3. **Хатуу хориглоно:** `nav('/foo')`, `<Link to="/foo">`-ийн адилаар flat
 *      absolute path ашиглах. Active tenant/company scope алдагдана.
 */

export const publicRoutes = {
  landing: '/',
  login: '/login',
  selectScope: '/select-scope',
  catalogShare: '/c/:token',
  invoicePublic: '/invoice/:token',
} as const;

export type PublicRouteKey = keyof typeof publicRoutes;

/**
 * Tenant + company prefix-ийн доор амьдардаг бүх page-ийн RELATIVE path.
 * Mounting-ыг `router.tsx`-д `path: '/:tenantId/:companyId'` parent route-аар.
 */
export const scopedRoutes = {
  dashboard: 'dashboard',
  rooms: 'rooms',
  items: 'items',
  catalogs: 'catalogs',
  catalogEdit: 'catalog-edit',
  catalogOrders: 'catalog-orders',
  shopStore: 'shop-store',
  customers: 'customers',
  vendors: 'vendors',
  priceGroups: 'price-groups',
  bankAccounts: 'bank-accounts',
  salesHistory: 'sales-history',
  salesSummary: 'sales-summary',

  purchases: 'purchases',
  purchaseEdit: 'purchase-edit',
  purchaseReturns: 'purchase-returns',
  purchaseReturnEdit: 'purchase-return-edit',

  sales: 'sales',
  saleEdit: 'sale-edit',
  salesReturns: 'sales-returns',
  salesReturnEdit: 'sales-return-edit',
  salePrint: 'sale-print',
  purchasePrint: 'purchase-print',

  ar: 'ar',
  arEdit: 'ar-edit',
  ap: 'ap',
  apEdit: 'ap-edit',

  reportItemQty: 'report-item-qty',
  reportTxHistory: 'report-tx-history',
  // G16 — дутуу байсан тайлангууд.
  reportPurchase: 'report-purchase',
  reportProfit: 'report-profit',
  reportAging: 'report-aging',

  chartOfAccounts: 'chart-of-accounts',
  glJournals: 'gl-journals',
  glJournalEdit: 'gl-journal-edit',
  fiscalPeriods: 'fiscal-periods',

  counts: 'counts',
  countEdit: 'count-edit',

  inReturns: 'returns',
  inReturnEdit: 'return-edit',

  transfers: 'transfers',
  transferEdit: 'transfer-edit',

  invoices: 'invoices',
  invoiceDetail: 'invoice-detail',

  companySettings: 'company-settings',
  terminals: 'terminals',
  profile: 'profile',
  tickets: 'tickets',
  roadmap: 'roadmap',

  pos: 'pos',
  posShifts: 'pos-shifts',
} as const;

export type ScopedRouteKey = keyof typeof scopedRoutes;

export interface RouteScope {
  tenantId: string;
  companyId: string;
}

/**
 * `/:tenantId/:companyId/<page>` бүтэцтэй URL үүсгэнэ.
 * @param routeKey  scopedRoutes-ийн ID (compile-time check).
 * @param scope     active tenant + company.
 * @param query     `?id=...` гэх мэт query params (optional).
 */
export function buildScopedPath(
  routeKey: ScopedRouteKey,
  scope: RouteScope,
  query?: Record<string, string | number | null | undefined>,
): string {
  const sub = scopedRoutes[routeKey];
  const base = `/${scope.tenantId}/${scope.companyId}/${sub}`;
  if (!query) return base;
  const entries = Object.entries(query).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  if (entries.length === 0) return base;
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
  return `${base}?${qs}`;
}

/**
 * URL pathname-ээс `:tenantId/:companyId` prefix-ийг авна.
 * `null` буцаавал scoped path биш гэсэн үг.
 */
export function parseScopeFromPath(
  pathname: string,
): RouteScope | null {
  const m = pathname.match(/^\/([^/]+)\/([^/]+)(?:\/|$)/);
  if (!m) return null;
  const [, tenantId, companyId] = m;
  if (!isLikelyGuid(tenantId) || !isLikelyGuid(companyId)) return null;
  return { tenantId, companyId };
}

const GUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function isLikelyGuid(value: string | null | undefined): value is string {
  return !!value && GUID_RE.test(value);
}

/**
 * Хуучин flat path-уудын map (legacy redirect-д). Шинэ кодонд ашиглахгүй.
 */
export const LEGACY_PATH_TO_ROUTE: Record<string, ScopedRouteKey> = {
  '/dashboard': 'dashboard',
  '/items': 'items',
  '/customers': 'customers',
  '/vendors': 'vendors',
  '/price-groups': 'priceGroups',
  '/bank-accounts': 'bankAccounts',
  '/sales-history': 'salesHistory',
  '/sales-summary': 'salesSummary',
  '/purchases': 'purchases',
  '/purchase-edit': 'purchaseEdit',
  '/sales': 'sales',
  '/sale-edit': 'saleEdit',
  '/sale-print': 'salePrint',
  '/purchase-print': 'purchasePrint',
  '/ar': 'ar',
  '/ar-edit': 'arEdit',
  '/ap': 'ap',
  '/ap-edit': 'apEdit',
  '/report-item-qty': 'reportItemQty',
  '/report-tx-history': 'reportTxHistory',
  '/counts': 'counts',
  '/count-edit': 'countEdit',
  '/returns': 'inReturns',
  '/return-edit': 'inReturnEdit',
  '/invoices': 'invoices',
  '/invoice-detail': 'invoiceDetail',
  '/company-settings': 'companySettings',
  '/terminals': 'terminals',
  '/profile': 'profile',
  '/tickets': 'tickets',
  '/roadmap': 'roadmap',
  '/pos': 'pos',
  // .html legacy
  '/dashboard.html': 'dashboard',
  '/items.html': 'items',
  '/customers.html': 'customers',
  '/vendors.html': 'vendors',
  '/sales.html': 'sales',
  '/purchases.html': 'purchases',
  '/ar.html': 'ar',
  '/ap.html': 'ap',
  '/invoices.html': 'invoices',
  '/profile.html': 'profile',
  '/pos.html': 'pos',
};

/**
 * @deprecated Use `publicRoutes` + `scopedRoutes` + `buildScopedPath`.
 *  Backwards-compat re-export for any consumer that still references `routes.X`
 *  from an absolute path. Safe to remove after Phase 3 sweep.
 */
export const routes = {
  landing: publicRoutes.landing,
  login: publicRoutes.login,
  // Бусад нь deprecated absolute path хэлбэрээр scope-гүй URL үүсгэх ба
  // `LegacyRedirect`-ээр scoped URL руу redirect хийгдэнэ. Шинэ кодонд
  // **БҮҮ** ашиглаарай.
  dashboard: '/dashboard',
  items: '/items',
  customers: '/customers',
  vendors: '/vendors',
  priceGroups: '/price-groups',
  bankAccounts: '/bank-accounts',
  salesHistory: '/sales-history',
  salesSummary: '/sales-summary',
  purchases: '/purchases',
  purchaseEdit: '/purchase-edit',
  sales: '/sales',
  saleEdit: '/sale-edit',
  salePrint: '/sale-print',
  purchasePrint: '/purchase-print',
  ar: '/ar',
  arEdit: '/ar-edit',
  ap: '/ap',
  apEdit: '/ap-edit',
  reportItemQty: '/report-item-qty',
  reportTxHistory: '/report-tx-history',
  reportPurchase: '/report-purchase',
  reportProfit: '/report-profit',
  reportAging: '/report-aging',
  invoices: '/invoices',
  invoiceDetail: '/invoice-detail',
  companySettings: '/company-settings',
  terminals: '/terminals',
  profile: '/profile',
  tickets: '/tickets',
  roadmap: '/roadmap',
  pos: '/pos',
} as const;

export type RouteKey = keyof typeof routes;
